import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/AuthFrame";
import { Later } from "@/components/Later";
import { Failure, NotFound } from "@/features/shell/Problem";
import { OrderEntry } from "@/features/order/OrderEntry";
import { ApiError } from "@/lib/api";
import {
  addressesQuery,
  draftQuery,
  emptyForm,
  formFromOrder,
  orderAsFormQuery,
  orderFormQuery,
  readDraft,
  type BookEntry,
  type OrderFormSettings,
} from "@/lib/orderEntry";

// Order invoeren (docs/partner-portal.md §14): a new order, from the concept if there is one (N5); one started from an
// earlier order (?from=, Herhalen and Opnieuw invoeren, N9); or an order changed (?edit=, N7).

interface OrderSearch {
  from?: string;
  edit?: string;
}

const NUMBER = /^[A-Za-z0-9-]{3,40}$/;

export const Route = createFileRoute("/_portal/order")({
  validateSearch: (search: Record<string, unknown>): OrderSearch => {
    const number = (value: unknown) => (typeof value === "string" && NUMBER.test(value) ? value : undefined);
    return { from: number(search.from), edit: number(search.edit) };
  },
  component: OrderScreen,
});

function OrderScreen() {
  const { t } = useTranslation();
  const { from, edit } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const settings = useQuery(orderFormQuery);
  const book = useQuery(addressesQuery);

  if (settings.isError || book.isError) return <Failure error={settings.error ?? book.error} onRetry={() => void Promise.all([settings.refetch(), book.refetch()])} />;
  if (!settings.data || !book.data) return <Loading />;
  if (!settings.data.canEnterOrders) {
    return (
      <div className="wl-portal-page" data-screen-label="Portaal Order invoeren">
        <h1 className="t-h2">{t("nav.order")}</h1>
        <Notice tone="neutral">{t("order.notAvailable")}</Notice>
      </div>
    );
  }

  const pick = (orderNumber: string) => void navigate({ search: { from: orderNumber } });
  if (edit) return <ChangeOrder key={`edit-${edit}`} number={edit} settings={settings.data} book={book.data} onPick={pick} />;
  if (from) return <CopyOrder key={`from-${from}`} number={from} settings={settings.data} book={book.data} onPick={pick} />;
  return <NewOrder key="new" settings={settings.data} book={book.data} onPick={pick} />;
}

interface Props {
  settings: OrderFormSettings;
  book: BookEntry[];
  onPick: (orderNumber: string) => void;
}

function defaultsOf(settings: OrderFormSettings): string[] {
  return settings.requirements.filter((requirement) => requirement.isDefault).map((requirement) => requirement.id);
}

/** A new order: the concept this person kept for this company, or an empty form. */
function NewOrder({ settings, book, onPick }: Props) {
  const draft = useQuery({ ...draftQuery, refetchOnWindowFocus: false });
  if (draft.isPending) return <Loading />;
  const kept = draft.data ? readDraft(draft.data.payload) : null;
  return (
    <OrderEntry
      initial={kept?.form ?? emptyForm(defaultsOf(settings))}
      initialStep={kept?.step}
      initialSavedAt={kept ? (draft.data?.savedAt ?? null) : null}
      editing={null}
      settings={settings}
      book={book}
      onPickEarlier={onPick}
    />
  );
}

/** Begin with an earlier order: the addresses, windows, goods and requirements; the days and the reference are new. */
function CopyOrder({ number, settings, book, onPick }: Props & { number: string }) {
  const order = useQuery({ ...orderAsFormQuery(number), refetchOnWindowFocus: false });
  if (order.isError) return <Problem error={order.error} onRetry={() => void order.refetch()} />;
  if (!order.data) return <Loading />;
  return <OrderEntry initial={formFromOrder(order.data, "copy")} editing={null} settings={settings} book={book} onPickEarlier={onPick} />;
}

/** Wijzigen (N7): an order typed in the portal, until it is planned; after that the portal says to get in touch. */
function ChangeOrder({ number, settings, book, onPick }: Props & { number: string }) {
  const { t } = useTranslation();
  const order = useQuery({ ...orderAsFormQuery(number), refetchOnWindowFocus: false });
  if (order.isError) return <Problem error={order.error} onRetry={() => void order.refetch()} />;
  if (!order.data) return <Loading />;
  if (!order.data.canChange) {
    return (
      <div className="wl-portal-page" data-screen-label="Portaal Order wijzigen">
        <h1 className="t-h2">{t("order.titleChange", { number })}</h1>
        <Notice tone="neutral">{t(`order.notChangeable.${order.data.notChangeableBecause ?? "closed"}`)}</Notice>
        <div>
          <Link to="/shipments" className="wl-btn wl-btn--secondary">
            {t("problem.toShipments")}
          </Link>
        </div>
      </div>
    );
  }
  return <OrderEntry initial={formFromOrder(order.data, "edit")} editing={number} settings={settings} book={book} onPickEarlier={onPick} />;
}

function Problem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="wl-portal-page">
      {error instanceof ApiError && error.status === 404 ? <NotFound /> : <Failure error={error} onRetry={onRetry} />}
    </div>
  );
}

function Loading() {
  return (
    <div className="wl-portal-page" aria-busy="true">
      <Later>
        <span className="wl-skeleton" style={{ width: "30%", height: "var(--space-8)" }} />
        <span className="wl-skeleton" style={{ height: "var(--space-12)" }} />
        <span className="wl-skeleton" style={{ height: "calc(var(--space-24) * 2)" }} />
      </Later>
    </div>
  );
}
